import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { EXPERT_LABELS } from './Expert';
import Editor from '../../common/Editor';
import ExpertProductApi from '../../../api/ExpertProductApi';
import { uploadImagesAndReplaceInContent } from '../post/PostWrite';
import UserContext from '../../../context/UserContext';

const DEFAULT_SERVICE = {
    service_name : '',
    service_price : ''
}

const DEFAULT_FAQ = {
    faq_question: '',
    faq_answer: ''
}

const getCategories = () => EXPERT_LABELS.slice(1);


const Service = ({ ser, idx, onServiceChange }) => {

    const handleInputChg = e => {
        const { value, name } = e.target;
        let processedValue = value;
        
        // 가격 필드인 경우 양수 정수만 허용
        if (name === 'service_price') {
            // 숫자가 아닌 문자 제거 (0-9만 허용)
            processedValue = value.replace(/[^0-9]/g, '');
        }
        
        const newService = {...ser, [name] : processedValue};
        onServiceChange(idx, newService);
    }

    return (
        <>
            <p className="field-tit">서비스 {idx+1})</p>
            <div className="input-colgroup">
                <input 
                    type="text" 
                    placeholder="서비스명" 
                    name="service_name" 
                    value={ser.service_name}
                    onChange={handleInputChg} 
                />
            </div>
            <div className="input-colgroup">
                <input 
                    type="number" 
                    placeholder="가격을 입력해주세요 (최소 1,000원)" 
                    name="service_price" 
                    value={ser.service_price}
                    onChange={handleInputChg}
                />
                <div className="input-unit">원</div>
            </div>
        </>
    )
}

const Faq = ({ faq, idx, onFaqChange }) => {

    const handleInputChg = e => {
        const { value, name } = e.target;
        const newFaq = { ...faq, [name]: value };
        onFaqChange(idx, newFaq);
    }

    return (
        <>
            <p className="field-tit">질문 {idx+1}</p>
            <div className="input-colgroup">
                <input
                    type="text"
                    placeholder="질문"
                    name="faq_question"
                    value={faq.faq_question}
                    onChange={handleInputChg}
                />
            </div>
            <div className="input-colgroup">
                <textarea
                    placeholder="답변"
                    name="faq_answer"
                    value={faq.faq_answer}
                    onChange={handleInputChg}
                />
            </div>
        </>
    )
}

const ExpertWrite = () => {
    const { productId = '' } = useParams();
    const navigate = useNavigate();
    const { user } = useContext(UserContext);
    const [productTitle, setProductTitle] = useState('');
    const [productCategory, setProductCategory] = useState(getCategories()[0].value);
    const [services, setServices] = useState([DEFAULT_SERVICE]);
    const [faqs, setFaqs] = useState([DEFAULT_FAQ]);
    const [content, setContent] = useState("");
    const [term, setTerm] = useState("");
    const [bannerImage, setBannerImage] = useState(null);
    const [bannerImagePreview, setBannerImagePreview] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const isUpdate = productId !== 'init';
    
    const addService = () => {
        if (services.length >= 3) return alert("상품은 최대 3개까지 등록 가능합니다.");
        setServices([...services, {...DEFAULT_SERVICE}]);
    };    

    const addQuestion = () => {
        setFaqs([...faqs, { ...DEFAULT_FAQ }]);
    };

    const handleCateChg = e => {
        setProductCategory(e.target.value);
    }

    const handleTitleChg = e => {
        const { value } = e.target;
        if (value.length >= 30) return;
        setProductTitle(value);
    }

    const handleServiceChg = (idx, newService) => {
        const newServices = [...services];
        newServices[idx] = newService;
        setServices(newServices);
    }

    const handleFaqChg = (idx, newFaq) => {
        const newFaqs = [...faqs];
        newFaqs[idx] = newFaq;
        setFaqs(newFaqs);
    }

    const handleEditorChange = (event, editor) => {
        setContent(editor.getData());
    };


    const handleFileInputChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // 파일 선택 시 미리보기만 생성
            const reader = new FileReader();
            reader.onload = (e) => {
                setBannerImagePreview(e.target.result);
            };
            reader.readAsDataURL(file);
            setBannerImage(file);
        }
    };

    const handleImageButtonClick = () => {
        document.getElementById('fileInput').click();
    };

    const removeImage = () => {
        setBannerImage(null);
        setBannerImagePreview('');
        document.getElementById('fileInput').value = '';
    };

    // 필수 항목 검증 함수
    const validateRequiredFields = () => {
        const errors = [];

        // 1. 상품명 검증
        if (!productTitle.trim()) {
            errors.push('상품 이름을 입력해주세요.');
        }

        // 2. 서비스 검증 (최소 1개, 서비스명과 가격 모두 입력)
        const validServices = services.filter(service => 
            service.service_name.trim() && (service.service_price + '').trim()
        );
        
        if (validServices.length === 0) {
            errors.push('최소 1개의 서비스 정보(서비스명, 가격)를 입력해주세요.');
        }

        for (let i = 0; i < services.length; i++) {
            if (services[i].service_price < 1000) {
                errors.push('서비스의 최소 금액은 1,000원 이상이어야 합니다.');
                break;
            }
        }

        // 3. 상세 정보 검증
        if (!content.trim() || content.trim() === '<p></p>' || content.trim() === '<p><br></p>') {
            errors.push('상세 정보를 입력해주세요.');
        }

        // 4. 취소 및 환불 규정 검증
        if (!term.trim()) {
            errors.push('취소 및 환불 규정을 입력해주세요.');
        }

        return errors;
    };

    // 2단계: 포스트 작성 (이미지 URL 포함)
    const handleRegClick = async () => {
        // 필수 항목 검증
        const validationErrors = validateRequiredFields();
        if (validationErrors.length > 0) {
            alert(validationErrors.join('\n'));
            return;
        }

        // 새 상품 등록 시에는 이미지 필수
        if (!isUpdate && !bannerImage) {
            alert('대표 이미지를 선택해주세요.');
            return;
        }

        // 내용 길이 체크 (TEXT 타입 제한: 640KB)
        // if (content && content.length > 600000) {
        //     alert('상세 설명이 너무 깁니다. 내용을 줄여주세요.');
        //     return;
        // }
        let updatedContent;
        try {
            const uploadResult = await uploadImagesAndReplaceInContent(content);
            updatedContent = uploadResult.content;
        } catch (err) {
            console.error(err);
            return alert(err?.message || '본문 이미지 업로드에 실패했습니다.');
        }
        if (!updatedContent) return alert('본문에 내용이 없습니다.');

        // 새 이미지가 선택된 경우에만 검증
        console.log('bannerImage>>>>>>>', bannerImage)
        if (bannerImage) {
            // 파일 검증
            if (bannerImage.size > 5 * 1024 * 1024) {
                alert('이미지 크기는 5MB 이하로 업로드해주세요.');
                return;
            }

            if (!bannerImage.type.startsWith('image/')) {
                alert('이미지 파일만 업로드 가능합니다.');
                return;
            }
        }

        setIsUploading(true);
        let uploadedImageUrl = '';

        // 새 이미지가 선택된 경우에만 업로드
        if (bannerImage) {
            try {
                // 1단계: 이미지 업로드
                const uploadResult = await ExpertProductApi.uploadBannerImage(bannerImage);
                uploadedImageUrl = uploadResult.imageUrl;
                
                if (uploadResult.warning) {
                    alert(uploadResult.warning);
                }
            } catch (error) {
                console.error('이미지 업로드 실패:', error);
                alert(error.message || '이미지 업로드에 실패했습니다.');
                setIsUploading(false);
                return;
            }
        } else if (bannerImagePreview) {
            // 기존 이미지가 있는 경우 그대로 사용
            uploadedImageUrl = bannerImagePreview;
        }

        try {
            // 2단계: 상품 등록/수정
            const product = {
                expert_product_title: productTitle,
                expert_product_category: productCategory,
                expert_product_banner_img: uploadedImageUrl,
                services,
                faqs,
                term: term || '',
                document: updatedContent
            };
            
            let rslt;
            if (isUpdate) {
                // 수정
                rslt = await ExpertProductApi.updateExpertProduct(productId, product);
            } else {
                // 등록
                rslt = await ExpertProductApi.createExpertProduct(product);
            }
            
            if (rslt.code === 'fail') {
                alert(`서비스 ${isUpdate ? '수정' : '등록'}에 실패했습니다. 다시 시도해주세요.`);
                return;
            }
            alert(`서비스가 성공적으로 ${isUpdate ? '수정' : '등록'}되었습니다.`);
            navigate('/mypage/expert');
        } catch (error) {
            console.error(`상품 ${isUpdate ? '수정' : '등록'} 실패:`, error);
            alert(`서비스 ${isUpdate ? '수정' : '등록'}에 실패했습니다. 다시 시도해주세요.`);
        } finally {
            setIsUploading(false);
        }
    }

    useEffect(() => {
        if (isUpdate) {
            ExpertProductApi.getExpertProduct(productId, true, true).then(rslt => {
                const { product, services, document, faqs, term } = rslt;
                setProductTitle(product.expert_product_title);
                setProductCategory(product.expert_product_category);
                setServices(services);
                setContent(document.content);
                setFaqs(faqs);
                setTerm(term.term_content);
                
                // 기존 배너 이미지가 있으면 미리보기 설정
                if (product.expert_product_banner_img) {
                    setBannerImagePreview(product.expert_product_banner_img);
                }
            });
        }
    }, [productId, isUpdate])


    if (user?.isInitiative) return <div className="vh-content"></div>

    if (!user || !user.userId) {
        alert('로그인이 필요합니다.');
        sessionStorage.setItem('referrer', window.location.href);
        return <Navigate to={'/login'} replace />
    }
    
    return (
        <div className="vh-content-sub">
            <div className="section-area">
                <div className="section-header">
                    <h1 className="section-header-title">{isUpdate ? '상품 수정' : '기본 정보'}</h1>
                </div>
                <div className="section-content">
                    <div className="section-title">
                        <h2>상품 카테고리 <span className="red">*</span></h2>
                    </div>
                    <div className="input-colgroup">
                        <select onChange={handleCateChg}>
                            {getCategories().map(c => <option value={c.value}>{c.name}</option>)}
                        </select>
                    </div>

                    <div className="section-title">
                        <h2>상품 이름 <span className="red">*</span></h2>
                    </div>
                    <div className="field-tipbox">
                        <div className="field-tipbox-tit">
                            <span>TIP</span>
                            <p>이런 이름이 좋아요</p>
                        </div>
                        <p>
                            진행하는 마케팅 서비스를 강조한 이름 ( 블로그, 플레이스, 리워드 등)<br /> 
                            추천 대상, 업종을 강조한 이름 ( 병원, 헬스장, 스마트스토어, 쿠팡 등 )
                        </p>
                    </div>
                    <div className="input-colgroup">
                        <input type="text" name="" maxLength="30" placeholder="상품 이름을 입력해주세요" onChange={handleTitleChg} value={productTitle} />
                        <div className="input-count">{productTitle.length}/30</div>
                    </div>

                    <div className="section-title">
                        <h2>가격 <span className="red">*</span></h2>
                    </div>
                    <div className="field-tipbox">
                        <div className="field-tipbox-tit">
                            <span>TIP</span>
                            <p>실제 판매되는 최저가격을 기준으로 올려주세요</p>
                        </div>
                        <p>모니터링 중 허위가격 발견시 서비스 중단이 될 수 있습니다</p>
                    </div>
                    {services.map((s, idx) => (
                        <Service key={'service_' + idx} ser={s} idx={idx} onServiceChange={handleServiceChg} />
                    ))}
                    <div id="serviceList"></div>
                    <div className="field-btn-area">
                        <button type="button" className="btn" onClick={addService}>
                            <span>서비스 추가</span>
                        </button>
                    </div>

                    <div className="section-title">
                        <h2>대표 이미지 <span className="red">*</span></h2>
                    </div>
                    <div className="field-tipbox">
                        <div className="field-tipbox-tit">
                            <span>TIP</span>
                            <p>이런 사진으로 대표이미지를 등록해주세요(권장사이즈 500X400)</p>
                        </div>
                        <p>가로로 긴 3:2 비율의 이미지<br />
                            주목도가 높은 선명한 이미지<br />
                            제공할 상품과 연관된 이미지
                        </p>
                    </div>
                    <div className="field-btn-area">
                        <input 
                            type="file" 
                            name="" 
                            hidden 
                            id="fileInput" 
                            accept="image/*"
                            onChange={handleFileInputChange}
                        />
                        <button 
                            type="button" 
                            className="upload-image-button" 
                            onClick={handleImageButtonClick}
                            disabled={isUploading}
                            style={{
                                backgroundImage: bannerImagePreview ? `url(${bannerImagePreview})` : '/static/img/icon/icon-images.svg',
                                backgroundSize: bannerImagePreview ? 'cover' : 'contain',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                                position: 'relative',
                                minHeight: '120px',
                                color: bannerImagePreview ? '#fff' : '#333',
                                textShadow: bannerImagePreview ? '1px 1px 2px rgba(0,0,0,0.7)' : 'none'
                            }}
                        >
                            {bannerImagePreview ? (
                                <div style={{ 
                                    position: 'absolute', 
                                    top: '5px', 
                                    right: '5px',
                                    background: 'rgba(0,0,0,0.7)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '25px',
                                    height: '25px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }} onClick={(e) => { e.stopPropagation(); removeImage(); }}>
                                    ×
                                </div>
                            ) : null}
                        </button>
                    </div>
                </div>
            </div>
            <div className="section-area">
                <div className="section-header">
                    <h1 className="section-header-title">상세 정보</h1>
                </div>
                <div className="section-content">
                    <div className="section-title">
                        <h2>상세 정보 <span className="red">*</span></h2>
                    </div>
                    <div className="field-tipbox">
                        <div className="field-tipbox-tit">
                            <span>TIP</span>
                            <p>아래 내용을 포함해주세요</p>
                        </div>
                        <pre>								서비스내용
                            추천대상
                            상품정보 가격, 옵션, 추가요금, 프로모션 등
                            업체정보 - 이력, 경력, 레퍼런스 등
                            기타 - 준비물, 준비사항 등
                        </pre>
                    </div>
                    <div className="editor-wrap expert-product">
                        <Editor data={content} onChange={handleEditorChange} />
                    </div>
                </div>
            </div>

            <div className="section-area">
                <div className="section-header">
                    <h1 className="section-header-title">자주 묻는 질문</h1>
                </div>
                <div className="section-content">
                    <div className="section-title">
                        <h2>(선택) 자주 묻는 질문</h2>
                    </div>
                    <div className="field-tipbox">
                        <div className="field-tipbox-tit">
                            <span>TIP</span>
                            <p>아래 내용을 포함해주세요</p>
                        </div>
                        <pre>								작업시간, AS 기간
                            고객이 신뢰할 수 있는 상세한 질문과 답변
                        </pre>
                    </div>
                    {faqs.map((faq, idx) => (
                        <Faq key={'faq_' + idx} faq={faq} idx={idx} onFaqChange={handleFaqChg} />
                    ))}
                    <div className="field-btn-area">
                        <button type="button" className="btn" onClick={addQuestion}>
                            <span>질문 추가</span>
                        </button>
                    </div>


                    <div className="section-title">
                        <h2>취소 및 환불 규정 <span className="red">*</span></h2>
                    </div>
                    <div className="field-tipbox">
                        <div className="field-tipbox-tit">
                            <span>TIP</span>
                            <p>아래 내용을 포함해주세요</p>
                        </div>
                        <pre>고객과의 분쟁 방지를 위한 구체적인 규정</pre>
                    </div>
                    <div className="field-term">
                        <textarea
                            onChange={e => setTerm(e.target.value)}
                            value={term}
                            placeholder="약관을 입력해주세요."
                        ></textarea>
                    </div>

                    <div className="form-control">
                        <button className="btn btn-primary" onClick={handleRegClick} disabled={isUploading}>
                            <span>{isUploading ? '업로드 중...' : (isUpdate ? '수정' : '등록')}</span>
                        </button>
                    </div>
                </div>

            </div>
        </div>
    )
}

export default ExpertWrite;